import { Button, Dialog, DialogProps, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { WorkflowNodeExpectedColumns } from 'api/model.ts';
import { useState } from 'react';
import { CustomFormat } from 'components/form/CustomFormats.ts';
import { JSONSchema7 } from 'json-schema';
import { Delete, Save } from '@mui/icons-material';
import CustomForm from 'components/form/CustomForm';

const initialValues: WorkflowNodeExpectedColumns = {
  input_columns: [],
  output_columns: [],
  removed_columns: [],
  replaces_dataframe: false,
};

const FORM_ID = 'expected-columns-form';

const FORM_SCHEMA = {
  type: 'object',
  properties: {
    input_columns: {
      type: 'array',
      title: 'Input Columns',
      description: 'Columns that are expected to be present as inputs to the node.',
      items: {
        type: 'string',
        format: CustomFormat.ColumnName,
      },
      uniqueItems: true,
    },
    output_columns: {
      type: 'array',
      title: 'Output Columns',
      description: 'New columns that are expected to be produced as outputs from the node.',
      items: {
        type: 'string',
        format: CustomFormat.ColumnName,
      },
      uniqueItems: true,
    },
    removed_columns: {
      type: 'array',
      title: 'Removed Columns',
      description: 'Columns that are expected to be removed by the node.',
      items: {
        type: 'string',
        format: CustomFormat.ColumnName,
      },
      uniqueItems: true,
    },
    replaces_dataframe: {
      type: 'boolean',
      title: 'Replaces Dataframe?',
      description:
        'Indicates whether the node replaces the entire dataframe. (All columns not listed in output will be removed)',
      default: false,
    },
  },
} as JSONSchema7;

type EditExpectedColumnsDialogProps = {
  open: boolean;
  onSubmit: (expectedColumns?: WorkflowNodeExpectedColumns) => void;
  onClose: () => void;
  initialData?: WorkflowNodeExpectedColumns;
} & DialogProps;

const EditExpectedColumnsDialog = ({ open, onClose, onSubmit, initialData }: EditExpectedColumnsDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState<WorkflowNodeExpectedColumns>(initialData ?? initialValues);

  const handleOnChange = (formData: WorkflowNodeExpectedColumns) => {
    setData(formData);
  };

  return (
    <Dialog fullScreen={fullScreen} maxWidth="lg" open={open} PaperComponent={Paper} onClose={onClose}>
      <div className="flex gap-2 flex-col m-10">
        <Typography variant="h5">Edit expected node columns</Typography>
        <div className="w-full">
          <div>
            <CustomForm
              schema={FORM_SCHEMA}
              onSubmit={onSubmit}
              onChange={handleOnChange}
              initialValues={data}
              formId={FORM_ID}
            />
            <div className="flex justify-end gap-2 mt-5">
              <Button color="secondary" variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button color="error" variant="outlined" onClick={() => onSubmit(undefined)} startIcon={<Delete />}>
                Remove
              </Button>
              <Button variant="contained" startIcon={<Save />} type="submit" form={FORM_ID}>
                Submit
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
export default EditExpectedColumnsDialog;
